import { useState, useEffect } from 'react';

import GalleryImage from './GalleryImage.jsx';
import InputContainer from '../InputContainer.jsx';
import { Box, Paper, ImageList, ImageListItem, Pagination, CircularProgress, Button } from '@mui/material';
import { config } from "../Const.js";
import { executeFetch } from '../Util.js';

function Gallery({ lockAspect, onSelect, searchStr = null }) {


    // validate image url by extension (case-insensitive)
    const isValidImd = (imd) => {
        const url = imd?.url;
        if (!url) return false;
        return /\.(png|jpe?g)$/i.test(url);
    }

    const getValidImdList = (imdList) => {
        return imdList
            ? imdList.map(imd => ({ ...imd, url: imd.url?.split(/[?#]/)[0] })).filter(imd => isValidImd(imd))
            : [];
    }

    const searchDataUrl = config.SERVER_URL + '/api/v1/admin/search/image?q=';

    // safe sessionStorage access helper
    const readStored = () => {
        try {
            if (typeof window === 'undefined' || !window.sessionStorage) return { storedSearchRaw: null, storedPageRaw: null };
            const storedSearchRaw = sessionStorage.getItem('gallery.searchQuery'); // null if not present
            const storedPageRaw = sessionStorage.getItem('gallery.pageNum'); // null if not present
            return { storedSearchRaw, storedPageRaw };
        } catch (e) {
            return { storedSearchRaw: null, storedPageRaw: null };
        }
    };

    const { storedSearchRaw, storedPageRaw } = readStored();

    // Normalize incoming prop
    const normalizedSearchStr = (searchStr && String(searchStr).trim()) ? String(searchStr).trim() : null;

    // Determine initialSearchQuery and initialPageNum according to the rules:
    // 1) if searchStr provided and different than stored -> use searchStr, page 1
    // 2) if searchStr provided and same as stored -> use stored pageNum
    // 3) if searchStr not provided -> use stored values (may be null)
    let initialSearchQuery;
    let initialPageNum;
    if (normalizedSearchStr !== null) {
        if (storedSearchRaw === null || storedSearchRaw !== normalizedSearchStr) {
            initialSearchQuery = normalizedSearchStr;
            initialPageNum = 1;
        } else {
            // equal to stored
            initialSearchQuery = storedSearchRaw;
            initialPageNum = storedPageRaw ? parseInt(storedPageRaw, 10) : 1;
        }
    } else {
        // searchStr not provided -> use stored values
        initialSearchQuery = storedSearchRaw !== null ? storedSearchRaw : null;
        initialPageNum = storedPageRaw ? parseInt(storedPageRaw, 10) : 1;
    }

    const [pageNum, setPageNum] = useState(initialPageNum);
    const [loading, setLoading] = useState(false);

    // Controls the actual performed search (used by effect)
    const [searchQuery, setSearchQuery] = useState(initialSearchQuery);

    // Input value (prefilled from remembered/default)
    const [searchQueryTemp, setSearchQueryTemp] = useState(initialSearchQuery ?? '');

    const [imdSlice, setImdSlice] = useState([]);
    const [pageCount, setPageCount] = useState(-1);
    const pageSize = 20;
    const columnCount = 5;

    const uploadUrl = config.SERVER_URL + "/api/v1/admin/image/upload";

    // On mount: if searchStr was provided and different than stored, persist it now (ensures sessionStorage matches state)
    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && window.sessionStorage && normalizedSearchStr !== null) {
                const stored = sessionStorage.getItem('gallery.searchQuery');
                if (stored === null || stored !== normalizedSearchStr) {
                    sessionStorage.setItem('gallery.searchQuery', normalizedSearchStr);
                    sessionStorage.setItem('gallery.pageNum', '1');
                }
            }
        } catch (e) {
            // ignore storage errors
        }
        // intentionally run only once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // persist to sessionStorage whenever searchQuery or pageNum changes
    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && window.sessionStorage) {
                if (searchQuery === null) {
                    sessionStorage.removeItem('gallery.searchQuery');
                } else {
                    sessionStorage.setItem('gallery.searchQuery', searchQuery);
                }
                sessionStorage.setItem('gallery.pageNum', String(pageNum));
            }
        } catch (e) {
            // ignore storage errors
        }
    }, [searchQuery, pageNum]);

    const handlePageChange = (e, page) => {
        setPageNum(page);
    }

    const handleInputChange = (value) => {
        setSearchQueryTemp(value);
    }

    const handleUploadImageSelected = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        setLoading(true);

        let responseDataHandler = (imd) => {
            setImdSlice([imd]);
            setLoading(false);
            setPageCount(1);
        }

        executeFetch(uploadUrl, "post", null, formData, null, responseDataHandler, null);
    };

    const fetchImageMetadataListByQuery = () => {
        // normalize blank -> null (means "no search")
        const norm = (searchQueryTemp && searchQueryTemp.trim()) ? searchQueryTemp.trim() : null;
        setPageNum(1);
        setSearchQuery(norm);
    }

    useEffect(() => {
        // only fetch when searchQuery is set (null means no search)
        if (!searchQuery) return;

        setLoading(true);
        setImdSlice([]);

        const url = `${searchDataUrl}${encodeURIComponent(searchQuery)}&pageNum=${pageNum}&pageSize=${pageSize}`;
        let responseDataHandler = (result) => {
            setLoading(false);
            const validImdList = getValidImdList(result.imdList);
            setImdSlice(validImdList);
            setPageCount(Math.ceil(result.resultCount / pageSize));
        }

        executeFetch(url, "get", null, null, null, responseDataHandler);
        // eslint-disable-next-line
    }, [searchQuery, pageNum]);

    return (
        <Box sx={{ height: '100%', width: '100%' }}>
            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', marginBottom: '20px' }}>
                <Box sx={{ width: '60%', display: 'flex', gap: '10px' }}>
                    <InputContainer
                        label='Search'
                        onInputChange={handleInputChange}
                        onEnter={fetchImageMetadataListByQuery}
                        value={searchQueryTemp}
                        defaultValue={searchQueryTemp}
                    />
                    <Button variant="contained" onClick={fetchImageMetadataListByQuery}>Search</Button>

                    {/* Upload button: put onChange on input (not Button) */}
                    <Button variant="contained" sx={{ ml: '10px' }} color="secondary" component="label">
                        Upload
                        <input
                            hidden
                            accept="image/*"
                            type="file"
                            onChange={handleUploadImageSelected}
                        />
                    </Button>
                </Box>

                {(pageCount > 1) &&
                    <Pagination sx={{ width: '40%' }} count={pageCount} color="primary" page={pageNum} onChange={handlePageChange} />
                }
            </Box>

            <Paper elevation={5} sx={{ padding: '10px', height: '90%', overflowY: 'auto' }}>
                {imdSlice.length === 0 && loading && <Box sx={{ padding: '20px' }}><CircularProgress /></Box>}

                <ImageList variant="masonry" cols={columnCount} gap={5}>
                    {!loading && imdSlice.map((imd) => (
                        <ImageListItem key={'item_' + imd.url}>
                            <GalleryImage lockAspect={lockAspect} key={imd.title} onSelect={onSelect} imd={imd} />
                        </ImageListItem>
                    ))}
                </ImageList>
            </Paper>
        </Box>
    );
};

export default Gallery;
